import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal from "./Reveal";
import { MENU, IMAGES } from "@/data/content";

export default function MenuSection() {
  const [active, setActive] = useState(0);
  const current = MENU[active];

  return (
    <section id="menu" data-testid="menu-section" className="relative bg-[#0D0D0D] py-24 md:py-32 px-6 md:px-10 border-t border-[#C8A24A]/10">
      <div className="mx-auto max-w-[1400px]">
        <Reveal className="text-center mb-12">
          <p className="text-[#C8A24A] uppercase text-xs tracking-[0.35em] mb-4">Our Kitchen</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl">
            The <span className="italic text-gold-gradient">Menu</span>
          </h2>
          <p className="mt-5 text-[#D6D6D6] font-light max-w-xl mx-auto text-sm md:text-base">
            A curated journey through India — from vibrant street chaat to the smoky
            embrace of the tandoor.
          </p>
        </Reveal>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 mb-14 border-y border-[#C8A24A]/15 py-5">
          {MENU.map((cat, i) => (
            <button
              key={cat.category}
              onClick={() => setActive(i)}
              data-testid={`menu-tab-${i}`}
              className={`relative text-xs md:text-sm tracking-[0.12em] uppercase transition-colors ${
                active === i ? "text-[#C8A24A]" : "text-[#D6D6D6]/60 hover:text-[#D6D6D6]"
              }`}
            >
              {cat.category}
              {active === i && (
                <motion.span layoutId="menu-underline" className="absolute -bottom-1.5 left-0 right-0 h-px bg-[#C8A24A]" />
              )}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-[1fr_0.9fr] gap-12 lg:gap-20 items-start">
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5 }}
              data-testid="menu-items-list"
            >
              <h3 className="font-display text-3xl md:text-4xl mb-8 text-white">{current.category}</h3>
              <div className="space-y-5">
                {current.items.map((item, idx) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, x: -14 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className="group flex items-end gap-3"
                  >
                    <span className="text-[#D6D6D6] group-hover:text-white transition-colors font-light">
                      {item.name}
                    </span>
                    <span className="flex-1 border-b border-dotted border-[#C8A24A]/30 mb-1.5" />
                    <span className="font-display text-lg text-[#C8A24A] shrink-0">${item.price}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="lg:sticky lg:top-28 space-y-6">
            <div className="relative h-72 md:h-96 overflow-hidden rounded-sm border border-[#C8A24A]/20 group">
              <img src={IMAGES.dishDark} alt="Signature dish" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] to-transparent" />
              <div className="absolute bottom-5 left-5">
                <p className="text-[#C8A24A] text-xs tracking-[0.25em] uppercase">Chef&apos;s Signature</p>
                <p className="font-display text-2xl text-white">Ekaa Special Angara Tikka</p>
              </div>
            </div>
            <div className="bg-[#181818] border border-[#C8A24A]/15 rounded-sm p-7 text-center">
              <p className="font-display text-2xl text-white mb-2">Reserve Your Table</p>
              <p className="text-[#D6D6D6]/80 font-light text-sm mb-5">
                Secure a seat for an unforgettable evening at Ekaa.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href="#contact" data-testid="menu-reserve-btn" className="btn-gold bg-gradient-to-r from-[#C8A24A] to-[#E0C067] text-[#0D0D0D] px-7 py-3 rounded-sm text-xs tracking-[0.16em] uppercase font-semibold">
                  Reserve Now
                </a>
                <a href="#order" data-testid="menu-order-btn" className="btn-gold border border-[#C8A24A]/60 text-[#C8A24A] px-7 py-3 rounded-sm text-xs tracking-[0.16em] uppercase">
                  Order Online
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
